import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import api from '../api/axios';
import { Role } from '../types/user';

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: Role;
  companyId?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  needsCompany: boolean;
  login: (credential: string) => Promise<boolean>;
  loginWithToken: (accessToken: string, userData: User) => void;
  setupCompany: (companyName: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTreasurer: boolean;
  isOperator: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsCompany, setNeedsCompany] = useState(false);
  const profileAbortRef = useRef<AbortController | null>(null);
  const loginInFlightRef = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');

    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    profileAbortRef.current = abortController;
    setToken(storedToken);

    api.get('/auth/profile', {
      headers: { Authorization: `Bearer ${storedToken}` },
      signal: abortController.signal,
    })
      .then((response) => {
        if (abortController.signal.aborted) return;
        const freshUser = response.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
        setNeedsCompany(!freshUser.companyId);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => abortController.abort();
  }, []);

  const login = async (credential: string): Promise<boolean> => {
    // Deduplicate concurrent login attempts (One Tap + button, React StrictMode double-invoke, etc.).
    // Without this, a second in-flight /auth/google/verify gets cancelled when we navigate
    // away on success, which surfaces as a noisy "Network Error" in the console.
    if (loginInFlightRef.current) return loginInFlightRef.current;

    // Abort any in-flight profile fetch to prevent stale data overwriting
    profileAbortRef.current?.abort();

    const run = (async () => {
      try {
        const response = await api.post('/auth/google/verify', { credential });
        const { accessToken, user: userData, needsCompany: nc } = response.data;

        localStorage.setItem('token', accessToken);
        localStorage.setItem('user', JSON.stringify(userData));

        setToken(accessToken);
        setUser(userData);
        setNeedsCompany(nc);
        setIsLoading(false);

        return nc;
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      } finally {
        loginInFlightRef.current = null;
      }
    })();

    loginInFlightRef.current = run;
    return run;
  };

  const loginWithToken = (accessToken: string, userData: User) => {
    // Abort any in-flight profile fetch to prevent stale data overwriting
    profileAbortRef.current?.abort();
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
    setNeedsCompany(!userData.companyId);
    setIsLoading(false);
  };

  const setupCompany = async (companyName: string) => {
    try {
      const response = await api.post('/auth/setup-company', { companyName });
      const { accessToken, user: userData } = response.data;

      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(accessToken);
      setUser(userData);
      setNeedsCompany(false);
    } catch (error) {
      console.error('Setup company failed:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setNeedsCompany(false);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    needsCompany,
    login,
    loginWithToken,
    setupCompany,
    logout,
    isAuthenticated: !!token,
    isAdmin: user?.role === 'admin',
    isTreasurer: user?.role === 'treasurer',
    isOperator: user?.role === 'operator',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
